/**
 * Centralized registry and helper utilities for public routes.
 * Ensures legal compliance pages (Privacy, Terms, Data Deletion), public catalogs,
 * and auth routes are completely public and NEVER trigger automated redirects to /login.
 */

export const PUBLIC_LEGAL_PATHS = [
  '/terms',
  '/terminos',
  '/terminos-y-condiciones',
  '/terms-and-conditions',
  '/privacy',
  '/privacidad',
  '/privacity',
  '/politica-privacidad',
  '/privacy-policy',
  '/data-deletion',
  '/eliminacion-datos',
  '/data-deletion-instructions',
  // In-app alias variations
  '/app/terms',
  '/app/terminos',
  '/app/privacy',
  '/app/privacidad',
  '/app/privacity',
  '/app/data-deletion',
  '/app/eliminacion-datos',
] as const

export const PUBLIC_AUTH_PATHS = [
  '/',
  '/login',
  '/register',
  '/registro',
  '/forgot-password',
  '/reset-password',
] as const

export const PUBLIC_MARKETING_PATHS = [
  '/reserva-tu-lugar',
] as const

/**
 * Normalizes a pathname by trimming trailing slashes and converting to lowercase.
 */
export function normalizePath(pathname: string): string {
  if (!pathname) return '/'
  const clean = pathname.replace(/\/+$/, '').toLowerCase()
  return clean || '/'
}

/**
 * Checks whether a given path is a public page that does not require authentication
 * and should never be automatically redirected to /login on 401s or token lapses.
 */
export function isPublicPath(pathname: string): boolean {
  const clean = normalizePath(pathname)

  // Auth pages
  if (PUBLIC_AUTH_PATHS.some((p) => clean === p)) return true

  // Marketing & public landing pages
  if (PUBLIC_MARKETING_PATHS.some((p) => clean === p)) return true

  // Legal & Meta compliance pages
  if (PUBLIC_LEGAL_PATHS.some((p) => clean === p)) return true

  // Public catalog: /:tenantSlug/catalog or /:tenantSlug/catalog/:productSlug
  // excluding /app/* and /admin/*
  if (/^\/(?!app(?:\/|$)|admin(?:\/|$))[^/]+\/catalog(?:\/.*)?$/i.test(clean)) {
    return true
  }

  // Public receipt: /app/orders/:id/receipt
  if (/^\/app\/orders\/[^/]+\/receipt(?:\/.*)?$/i.test(clean)) {
    return true
  }

  return false
}

/**
 * Determines whether the layout should run in fullscreen mode (without Header/Sidebar/BottomNav).
 * Covers public pages, onboarding, order receipts, and admin sections.
 */
export function isFullscreenLayoutPath(pathname: string): boolean {
  const clean = normalizePath(pathname)

  if (isPublicPath(clean)) return true
  if (clean.startsWith('/onboarding') || clean.startsWith('/app/onboarding')) return true
  if (clean.startsWith('/admin')) return true

  return false
}
